import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Box, CircularProgress, Typography } from '@mui/material';
import axiosInstance from '@/setup/axios/axiosInstance';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

import { AppPagination } from '@/__core/components';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

import { InventoryStockTransactionRow } from '../types';

export type StockTransactionListHandle = {
  requestRefresh: () => void;
};

interface StockTransactionListProps {
  companyId?: string;
  searchQuery?: string;
  stockState?: string;
  onSelectRow?: (row: InventoryStockTransactionRow) => void;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveListCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'all' || resolved === 'global' || !UUID_PATTERN.test(resolved)) {
    return undefined;
  }
  return resolved;
};

interface ApiStockTransactionItem {
  id: string;
  company_id?: string;
  company_name?: string;
  store_id?: string;
  store_code?: string;
  store_name?: string;
  item_id?: string;
  item_code?: string;
  item_name?: string;
  txn_type?: string;
  quantity_delta?: string | number;
  unit_price?: string | number;
  line_amount?: string | number;
  posted_at?: string;
  document_type?: string;
  document_id?: string | null;
  reference_no?: string;
  lot_no?: string;
  bin_location?: string;
  note?: string;
  created_at?: string;
  updated_at?: string;
}

const mapApiStockTransaction = (row: ApiStockTransactionItem): InventoryStockTransactionRow => ({
  id: row.id,
  company_id: row.company_id || '',
  company_name: row.company_name || '',
  store_id: row.store_id || '',
  store_code: row.store_code || '',
  store_name: row.store_name || '',
  item_id: row.item_id || '',
  item_code: row.item_code || '',
  item_name: row.item_name || '',
  txn_type: row.txn_type || '',
  quantity_delta: Number(row.quantity_delta || 0),
  unit_price: Number(row.unit_price || 0),
  line_amount: Number(row.line_amount || 0),
  posted_at: row.posted_at || '',
  document_type: row.document_type || '',
  document_id: row.document_id || '',
  reference_no: row.reference_no || '',
  lot_no: row.lot_no || '',
  bin_location: row.bin_location || '',
  note: row.note || '',
  created_at: row.created_at || '',
  updated_at: row.updated_at || '',
});


const transactionLabel = (row: InventoryStockTransactionRow) => {
  switch ((row.txn_type || '').toLowerCase()) {
    case 'receipt':
      return 'Nhập kho';
    case 'issue':
      return 'Xuất kho';
    case 'return':
      return 'Hoàn trả';
    case 'adjustment':
      return 'Điều chỉnh';
    default:
      return row.txn_type || '-';
  }
};

const formatNumber = (value: number, digits: number = 0) =>
  Number(value || 0).toLocaleString('vi-VN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('vi-VN');
};

const StockTransactionList = forwardRef<StockTransactionListHandle, StockTransactionListProps>(function StockTransactionList(
  { companyId, searchQuery = '', stockState = 'all', onSelectRow },
  ref
) {
  const [rows, setRows] = useState<InventoryStockTransactionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize: number = DEFAULT_PAGE_SIZE;

  const resolvedCompanyId = useMemo(() => resolveListCompanyId(companyId), [companyId]);

  useEffect(() => {
    setPage(1);
  }, [resolvedCompanyId, searchQuery, stockState]);

  useEffect(() => {
    let active = true;

    const fetchStockTransactions = async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams();
        if (resolvedCompanyId) {
          query.append('company', resolvedCompanyId);
        }
        if (searchQuery.trim()) {
          query.append('search', searchQuery.trim());
        }
        if (stockState && stockState !== 'all') {
          query.append('txn_type', stockState);
        }

        const qs = query.toString();
        const response = await axiosInstance.get<{ results?: ApiStockTransactionItem[] } | ApiStockTransactionItem[]>(
          `/inventory/stock-transactions/${qs ? `?${qs}` : ''}`
        );

        if (!active) return;

        const data = response.data;
        const payload = Array.isArray(data) ? data : (data.results || []);
        setRows(payload.map(mapApiStockTransaction));
      } catch (_error) {
        if (!active) return;
        setRows([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(() => {
      void fetchStockTransactions();
    }, 200);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [resolvedCompanyId, searchQuery, stockState, reloadTick]);

  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: () => setReloadTick((current) => current + 1),
    }),
    []
  );

  const totalCount = rows.length;
  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return rows.slice(startIndex, startIndex + pageSize);
  }, [page, pageSize, rows]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, height: 220 }}>
        <Typography variant='body1' color='text.secondary'>Không có giao dịch kho</Typography>
        <Typography variant='body2' color='text.secondary'>Thử thay đổi bộ lọc hoặc làm mới danh sách.</Typography>
      </Box>
    );
  }

  const columns: Array<{
    id: string;
    label: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (_: unknown, row: InventoryStockTransactionRow) => React.ReactNode;
  }> = [
    { id: 'reference_no', label: 'Số tham chiếu', width: '150px', render: (_: unknown, row: InventoryStockTransactionRow) => <Typography sx={{ fontWeight: 600 }}>{row.reference_no || '-'}</Typography> },
    { id: 'txn_type', label: 'Loại GD', width: '130px', render: (_: unknown, row: InventoryStockTransactionRow) => {
      const colorMap: Record<string, string> = { receipt: '#059669', issue: '#dc2626', adjustment: '#d97706', return: '#7c3aed' };
      const color = colorMap[(row.txn_type || '').toLowerCase()] || 'inherit';
      return <Typography sx={{ color, fontWeight: 600 }}>{transactionLabel(row)}</Typography>;
    } },
    { id: 'item_code', label: 'Mã vật tư', width: '140px', render: (_: unknown, row: InventoryStockTransactionRow) => <Typography sx={{ fontWeight: 600 }}>{row.item_code}</Typography> },
    { id: 'item_name', label: 'Tên vật tư', width: '240px' },
    { id: 'store_name', label: 'Kho', width: '180px', render: (_: unknown, row: InventoryStockTransactionRow) => row.store_name || '-' },
    {
      id: 'quantity_delta',
      label: 'SL biến động',
      width: '130px',
      align: 'right',
      render: (_: unknown, row: InventoryStockTransactionRow) => (
        <Typography sx={{ color: row.quantity_delta < 0 ? '#dc2626' : '#059669', fontWeight: 600 }}>
          {formatNumber(row.quantity_delta, 3)}
        </Typography>
      ),
    },
    { id: 'unit_price', label: 'Đơn giá', width: '130px', align: 'right', render: (_: unknown, row: InventoryStockTransactionRow) => formatNumber(row.unit_price, 2) },
    { id: 'line_amount', label: 'Giá trị', width: '130px', align: 'right', render: (_: unknown, row: InventoryStockTransactionRow) => formatNumber(row.line_amount, 2) },
    { id: 'posted_at', label: 'Thời điểm ghi sổ', width: '180px', render: (_: unknown, row: InventoryStockTransactionRow) => formatDateTime(row.posted_at) },
    { id: 'lot_no', label: 'Lô', width: '120px', render: (_: unknown, row: InventoryStockTransactionRow) => row.lot_no || '-' },
    { id: 'bin_location', label: 'Vị trí kệ', width: '120px', render: (_: unknown, row: InventoryStockTransactionRow) => row.bin_location || '-' },
  ];

  return (
    <Box sx={sharedListStyles.container}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DataTableModule
          data={pagedRows}
          columns={columns}
          leftFilterWidth={0}
          onRowSelect={() => undefined}
          onSelectAll={() => undefined}
          onRowClick={(row) => onSelectRow?.(row as InventoryStockTransactionRow)}
          getRowId={(row) => (row as InventoryStockTransactionRow).id}
          isRowSelected={() => false}
          areAllRowsSelected={false}
          areSomeRowsSelected={false}
          showCheckbox={false}
        />
        <AppPagination
          count={totalCount}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          itemLabel='giao dịch kho'
          onPageChange={(_event, nextPage) => setPage(nextPage)}
        />
      </Box>
    </Box>
  );
});

export default StockTransactionList;
